import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DndContext, useSensor, useSensors, PointerSensor, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import MUIDataTable from 'mui-datatables';
import { TableRow, TableCell } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import styles from '../CPTValues/CPTValues.module.scss';

const SortableRow = ({ 
  children, 
  id, 
  isGroupStart, 
  disabled = false, 
  isCollapsed, 
  isDragOverlay = false, 
  ...props 
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: isDragOverlay ? undefined : CSS.Transform.toString(transform),
    transition: isDragOverlay ? undefined : transition,
    opacity: isDragging && !isDragOverlay ? 0.5 : 1,
  };

  const dragOverlayStyle = isDragOverlay ? {
    height: '60px',
    minHeight: '60px',
    maxHeight: '60px',
    backgroundColor: 'white',
    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
    borderRadius: '4px',
    border: '2px solid #1976d2',
    zIndex: 9999,
  } : {};

  if (!isGroupStart && isCollapsed && !isDragOverlay) {
    return null;
  }

  return (
    <TableRow 
      ref={setNodeRef} 
      style={{ ...style, ...dragOverlayStyle }} 
      {...props}
    >
      <TableCell
        style={{
          cursor: isGroupStart && !disabled ? 'grab' : 'default',
          padding: '8px',
          width: '40px',
          backgroundColor: 'transparent',
          borderBottom: isDragOverlay ? 'none' : '1px solid rgba(224, 224, 224, 1)',
        }}
        {...(isGroupStart && !disabled ? attributes : {})}
        {...(isGroupStart && !disabled ? listeners : {})}
      >
        {isGroupStart ? (
          <DragIndicatorIcon className={styles.lightDragIcon} />
        ) : null}
      </TableCell>
      {children}
    </TableRow>
  );
};

SortableRow.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string.isRequired,
  isGroupStart: PropTypes.bool,
  disabled: PropTypes.bool,
  isDragOverlay: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

// Extracted DnDRow component
const DnDRowComponent = ({ 
  dataIndex, 
  flatData, 
  dataGroups, 
  collapsedGroups, 
  disabled, 
  tableColumns 
}) => {
  const currentOrderNumber = flatData[dataIndex]?.orderNumber;
  const isOrderStart = dataIndex === 0 || flatData[dataIndex - 1]?.orderNumber !== currentOrderNumber;

  const orderGroup = dataGroups.find(group => group.orderNumber === currentOrderNumber);
  const sortableId = orderGroup ? orderGroup.id : `order-${currentOrderNumber}`;
  const isCollapsed = collapsedGroups?.[currentOrderNumber];

  if (!isOrderStart && isCollapsed) {
    return null;
  }

  return (
    <SortableRow
      key={`row-${dataIndex}`}
      id={sortableId}
      isGroupStart={isOrderStart}
      disabled={disabled}
      isCollapsed={isCollapsed}
    >
      {tableColumns.slice(1).map((col, colIndex) => {
        const rowData = flatData[dataIndex];
        let cellContent;

        if (col.options?.customBodyRender) {
          cellContent = col.options.customBodyRender(rowData[col.name], {
            rowIndex: dataIndex,
            rowData,
            tableMeta: { rowIndex: dataIndex },
          });
        } else if (typeof rowData[col.name] === 'object' && rowData[col.name] !== null) {
          cellContent = '';
        } else {
          cellContent = rowData[col.name];
        }

        return (
          <TableCell 
            key={`${col.name}-${colIndex}`} 
            style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
          >
            {cellContent}
          </TableCell>
        );
      })}
    </SortableRow>
  );
};

DnDRowComponent.propTypes = {
  dataIndex: PropTypes.number.isRequired,
  flatData: PropTypes.array.isRequired,
  dataGroups: PropTypes.array.isRequired,
  collapsedGroups: PropTypes.object,
  disabled: PropTypes.bool,
  tableColumns: PropTypes.array.isRequired,
};

const DnDRow = (props) => <DnDRowComponent {...props} />;

// Move CustomRowRenderer outside and make it a proper component
const CustomRowRenderer = React.memo(({ 
  row, 
  dataIndex, 
  flatData, 
  dataGroups, 
  collapsedGroups, 
  disabled, 
  tableColumns 
}) => (
  <DnDRowComponent
    dataIndex={dataIndex}
    flatData={flatData}
    dataGroups={dataGroups}
    collapsedGroups={collapsedGroups}
    disabled={disabled}
    tableColumns={tableColumns}
  />
));

CustomRowRenderer.propTypes = {
  row: PropTypes.array,
  dataIndex: PropTypes.number.isRequired,
  flatData: PropTypes.array.isRequired,
  dataGroups: PropTypes.array.isRequired,
  collapsedGroups: PropTypes.object,
  disabled: PropTypes.bool,
  tableColumns: PropTypes.array.isRequired,
};

function enhancedCollisionDetection(args) {
  const { droppableContainers, pointerCoordinates } = args;
  if (!pointerCoordinates) {
    return closestCenter(args);
  }

  const centerY = pointerCoordinates.y;
  let closestDistance = Infinity;
  let closestDroppable = null;

  for (const droppable of droppableContainers) {
    const node = droppable.node.current;
    if (!droppable.rect.current || !node) continue;
    if (node.hasAttribute('data-collapsed-child')) continue;

    const rect = droppable.rect.current;
    const droppableCenterY = rect.top + rect.height / 2;
    const distance = Math.abs(centerY - droppableCenterY);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestDroppable = droppable;
    }
  }

  return closestDroppable ? [{ id: closestDroppable.id }] : [];
}

const DnDTable = ({
  title = '',
  flatData,
  tableColumns,
  updateData,
  disabled,
  collapsedGroups,
  dataGroups,
  onCollapseGroup,
  onExpandGroup,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = useCallback((event) => {
    const activeGroup = dataGroups.find(group => group.id === event.active.id);
    if (activeGroup) {
      onCollapseGroup(activeGroup.orderNumber);
    }
  }, [dataGroups, onCollapseGroup]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    const activeGroup = dataGroups.find(group => group.id === active.id);
    if (activeGroup) {
      onExpandGroup(activeGroup.orderNumber);
    }

    if (!over || active.id === over.id) return;

    const activeGroupIndex = dataGroups.findIndex(group => group.id === active.id);
    const overGroupIndex = dataGroups.findIndex(group => group.id === over.id);

    if (activeGroupIndex === -1 || overGroupIndex === -1) return;

    const reorderedGroups = arrayMove(dataGroups, activeGroupIndex, overGroupIndex);
    updateData(reorderedGroups);
  }, [dataGroups, onExpandGroup, updateData]);

  // Create a stable reference to the custom row render function
  const customRowRender = useCallback((row, dataIndex) => (
    <CustomRowRenderer
      row={row}
      dataIndex={dataIndex}
      flatData={flatData}
      dataGroups={dataGroups}
      collapsedGroups={collapsedGroups}
      disabled={disabled}
      tableColumns={tableColumns}
    />
  ), [flatData, dataGroups, collapsedGroups, disabled, tableColumns]);

  const customOptions = useMemo(() => ({
    filter: false,
    sort: false,
    selectableRows: 'none',
    responsive: 'standard',
    print: false,
    pagination: false,
    search: false,
    download: false,
    viewColumns: false,
    elevation: 0,
    customRowRender,
  }), [customRowRender]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={enhancedCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext
        items={dataGroups?.map(group => group.id) || []}
        strategy={verticalListSortingStrategy}
      >
        <div id={title ? `${title.toLowerCase().replace(/\s+/g, '-')}-table` : 'dnd-table'}>
          <MUIDataTable
            title={title || ''}
            data={flatData || []}
            columns={tableColumns}
            options={customOptions}
          />
        </div>
      </SortableContext>
    </DndContext>
  );
};

DnDTable.propTypes = {
  title: PropTypes.string,
  flatData: PropTypes.array.isRequired,
  tableColumns: PropTypes.array.isRequired,
  updateData: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  collapsedGroups: PropTypes.object,
  dataGroups: PropTypes.array.isRequired,
  onCollapseGroup: PropTypes.func.isRequired,
  onExpandGroup: PropTypes.func.isRequired,
};

export { SortableRow };
export default DnDTable;